import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { api, setSession, clearSession, getUser, getToken, getEmpresas, setEmpresas, Usuario, EmpresaVinculo } from './api';

export interface CadastroDados {
  empresa_nome: string;
  plano: string;
  admin_nome: string;
  admin_email: string;
  admin_senha: string;
  whatsapp?: string;
  documento?: string;
  website?: string;
}

export interface LoginResultado {
  precisaEscolher: boolean;
  usuario: Usuario | null;
  empresas: EmpresaVinculo[];
}

export interface SuporteResultado {
  precisaEscolher: boolean;
  usuario: Usuario | null;
  empresas: EmpresaVinculo[];
  usuarioSuporte?: {
    id: number;
    nome: string;
    email: string;
    perfil: string;
  };
}

export interface PerfilDados {
  nome: string;
  email: string;
  empresa_nome?: string;
  telefone?: string | null;
  whatsapp?: string | null;
  senha?: string;
}

interface AuthValue {
  user: Usuario | null;
  empresas: EmpresaVinculo[];
  login: (email: string, senha: string, manterConectado?: boolean) => Promise<LoginResultado>;
  selecionarEmpresa: (empresaId: number) => Promise<Usuario>;
  acessarSuporte: (email: string, empresaId?: number) => Promise<SuporteResultado>;
  atualizarPerfil: (dados: PerfilDados) => Promise<Usuario>;
  cadastrar: (dados: CadastroDados) => Promise<void>;
  logout: () => Promise<void>;
  isPremium: boolean;
  isConsumoDisponivel: boolean;
  isOrcamentoOsDisponivel: boolean;
  isFinanceiroDisponivel: boolean;
  isIntegracaoPagamentosDisponivel: boolean;
  isServicosDisponivel: boolean;
  isOsSimplificadaDisponivel: boolean;
  // "RETIRAR UNIDADE - LOCAL - PISO": esconde a hierarquia em todo o sistema.
  // Gate unico para nao existirem varias leituras da mesma permissao.
  isSemUnidadeLocalPiso: boolean;
  // "RETIRAR TELA PMOC": gate unico do modulo PMOC no frontend.
  isSemPmoc: boolean;
  // "EXCLUIR UNIDADE/PISO/LOCAL SEM HISTORICO": libera o botao Excluir em Cadastros.
  isUnidadeLocalDeleteDisponivel: boolean;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Usuario | null>(getUser());
  const [empresas, setEmpresasState] = useState<EmpresaVinculo[]>(getEmpresas());

  useEffect(() => {
    let ativo = true;
    async function atualizarSessaoSalva() {
      const token = getToken();
      if (!token) return;
      try {
        const data = await api<Usuario | { usuario: Usuario }>('/usuarios/me.php', { redirectOn401: false });
        const usuario = 'usuario' in data ? data.usuario : data;
        if (!ativo || !usuario) return;
        setSession(token, usuario);
        setUser(usuario);
      } catch {
        // Token sem empresa selecionada ou sessao expirada ja e tratado pelo api().
      }
    }
    atualizarSessaoSalva();
    return () => { ativo = false; };
  }, []);

  async function login(email: string, senha: string, manterConectado = false): Promise<LoginResultado> {
    const data = await api<{ token: string; usuario?: Usuario; empresas?: EmpresaVinculo[]; precisa_escolher?: boolean }>(
      '/login.php', { method: 'POST', body: { email, senha, manter_conectado: manterConectado } },
    );
    const lista = data.empresas || [];
    setEmpresas(lista);
    setEmpresasState(lista);
    if (data.usuario) {
      // admin_geral ou 1 vínculo: já entra
      setSession(data.token, data.usuario);
      setUser(data.usuario);
      return { precisaEscolher: false, usuario: data.usuario, empresas: lista };
    }
    // 2+ vínculos: guarda o token (sem usuário) e devolve a lista p/ o seletor
    setSession(data.token, null);
    setUser(null);
    return { precisaEscolher: true, usuario: null, empresas: lista };
  }

  // Define a empresa ativa da sessão (seletor pós-login e "trocar empresa").
  async function selecionarEmpresa(empresaId: number): Promise<Usuario> {
    const data = await api<{ usuario: Usuario }>('/empresas/empresa_selecionar.php', { method: 'POST', body: { empresa_id: empresaId } });
    setSession(getToken(), data.usuario);
    setUser(data.usuario);
    return data.usuario;
  }

  async function acessarSuporte(email: string, empresaId?: number): Promise<SuporteResultado> {
    const data = await api<{
      usuario?: Usuario;
      empresas?: EmpresaVinculo[];
      precisa_escolher?: boolean;
      usuario_suporte?: SuporteResultado['usuarioSuporte'];
    }>('/suporte/acessar_usuario.php', { method: 'POST', body: { email, empresa_id: empresaId } });

    const lista = data.empresas || [];
    if (lista.length) {
      setEmpresas(lista);
      setEmpresasState(lista);
    }
    if (data.usuario) {
      setSession(getToken(), data.usuario);
      setUser(data.usuario);
      return { precisaEscolher: false, usuario: data.usuario, empresas: lista };
    }
    return {
      precisaEscolher: Boolean(data.precisa_escolher),
      usuario: null,
      empresas: lista,
      usuarioSuporte: data.usuario_suporte,
    };
  }

  async function atualizarPerfil(dados: PerfilDados): Promise<Usuario> {
    const data = await api<{ usuario: Usuario }>('/usuarios/me.php', { method: 'PUT', body: dados });
    if (data.usuario.empresa_id && data.usuario.empresa_nome) {
      const atualizadas = empresas.map((emp) => (
        emp.empresa_id === data.usuario.empresa_id
          ? { ...emp, empresa_nome: data.usuario.empresa_nome || emp.empresa_nome }
          : emp
      ));
      setEmpresas(atualizadas);
      setEmpresasState(atualizadas);
    }
    setSession(getToken(), data.usuario);
    setUser(data.usuario);
    return data.usuario;
  }

  // Cadastro com aprovação: cria a conta como "pendente". NÃO faz login (sem token).
  async function cadastrar(dados: CadastroDados): Promise<void> {
    await api('/empresas/cadastro_publico.php', { method: 'POST', body: dados });
  }

  async function logout(): Promise<void> {
    try { await api('/logout.php', { method: 'POST' }); } catch { /* ignora */ }
    clearSession();
    setUser(null);
    setEmpresasState([]);
  }

  const isPremium = user?.plano === 'premium';
  const isConsumoDisponivel = Boolean(
    user?.empresa_id
    && user?.perfil !== 'solicitante'
    && isPremium
    && user?.modo_visualizacao !== 'oficina'
    && user?.registro_consumo_habilitado,
  );
  const isOrcamentoOsDisponivel = Boolean(user?.empresa_id && user?.orcamento_os_habilitado);
  // Menus "Financeiro" e "Integração": gate único usado pelo menu (Layout) e pelas rotas (App),
  // para a regra não ficar duplicada em dois lugares e divergir depois.
  const perfilAdministraEmpresa = ['admin_empresa', 'supervisor', 'admin_geral'].includes(user?.perfil || '');
  const isFinanceiroDisponivel = Boolean(user?.empresa_id && perfilAdministraEmpresa && user?.modo_financeiro_habilitado);
  const isIntegracaoPagamentosDisponivel = Boolean(user?.empresa_id && perfilAdministraEmpresa && user?.integracao_pagamentos_habilitado);
  // Menu "Serviços": permissão de empresa "SERVIÇOS" (tabela de preços de serviço).
  // Mesmo gate do menu (Layout) e da rota (App), para não divergirem.
  const isServicosDisponivel = Boolean(user?.empresa_id && perfilAdministraEmpresa && user?.servicos_habilitado);
  // Fluxo "OS simplificada": permissao de empresa "CRIACAO DE OS SIMPLIFICADA".
  // Sem restricao de perfil de proposito - abrir OS e liberado para qualquer
  // perfil autenticado (os/os_criar.php nao exige gestor), e quem nao pode ver
  // valor monetario recebe a lista de servicos sem preco pelo proprio backend.
  // Gate unico usado pela rota (App) e pela lista de OS, para nao divergirem.
  // Vale para qualquer perfil e qualquer modo: e uma decisao de como a empresa
  // trabalha, nao um recurso liberado por plano.
  const isSemUnidadeLocalPiso = Boolean(user?.retirar_unidade_local_piso_habilitado);
  const isSemPmoc = Boolean(user?.retirar_tela_pmoc_habilitado);
  const isUnidadeLocalDeleteDisponivel = Boolean(user?.unidade_local_delete_habilitado);
  const isOsSimplificadaDisponivel = Boolean(
    user?.empresa_id && user?.modo_visualizacao !== 'oficina' && user?.criacao_os_simplificada_habilitado,
  );
  return <AuthCtx.Provider value={{ user, empresas, login, selecionarEmpresa, acessarSuporte, atualizarPerfil, cadastrar, logout, isPremium, isConsumoDisponivel, isOrcamentoOsDisponivel, isFinanceiroDisponivel, isIntegracaoPagamentosDisponivel, isServicosDisponivel, isOsSimplificadaDisponivel, isSemUnidadeLocalPiso, isSemPmoc, isUnidadeLocalDeleteDisponivel }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}

export function Protegido({ children }: { children: ReactNode }) {
  if (!getToken()) return <Navigate to="/login" replace />;
  return <>{children}</>;
}
